import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteEvent, listEventsAdmin } from '../../api/events';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';
import { EventItem } from '../../types/event';

function formatPrice(cents: number) {
  return cents === 0 ? 'Free' : `$${(cents / 100).toFixed(2)}`;
}

function formatDateTime(eventDate: string | null) {
  if (!eventDate) return '—';
  return new Date(eventDate).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function EventsAdminPage() {
  const navigate = useNavigate();
  const { request: listRequest } = useApiCallWithAuth(listEventsAdmin);
  const { request: deleteRequest } = useApiCallWithAuth(deleteEvent);

  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EventItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  function loadEvents() {
    setLoading(true);
    listRequest()
      .then(setEvents)
      .catch(() => setError('Failed to load events'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadEvents(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRequest(deleteTarget.eventGuid);
      setDeleteTarget(null);
      loadEvents();
    } catch {
      setError('Failed to delete event');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Box sx={{ pt: 3 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
        <Typography variant='h5' fontWeight={700}>Events Admin</Typography>
        <Button variant='contained' startIcon={<AddIcon />} onClick={() => navigate('/events/new')}>
          New Event
        </Button>
      </Stack>

      {loading && <CircularProgress size={24} />}
      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}

      {!loading && !error && events.length === 0 && (
        <Typography color='text.secondary'>No events yet.</Typography>
      )}

      {!loading && events.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Price</strong></TableCell>
              <TableCell><strong>Capacity</strong></TableCell>
              <TableCell><strong>Spots Left</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align='right'><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map(event => (
              <TableRow key={event.eventGuid} hover>
                <TableCell><Typography variant='body2'>{event.name}</Typography></TableCell>
                <TableCell><Typography variant='body2'>{formatPrice(event.priceCents)}</Typography></TableCell>
                <TableCell><Typography variant='body2'>{event.capacity}</Typography></TableCell>
                <TableCell><Typography variant='body2'>{event.spotsRemaining}</Typography></TableCell>
                <TableCell>
                  <Typography variant='body2' color='text.secondary'>
                    {formatDateTime(event.eventDate)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={event.isActive ? 'Active' : 'Inactive'}
                    size='small'
                    color={event.isActive ? 'success' : 'default'}
                    variant={event.isActive ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell align='right'>
                  <Stack direction='row' spacing={0.5} justifyContent='flex-end'>
                    <Tooltip title='View registrants'>
                      <IconButton size='small' onClick={() => navigate(`/events/${event.eventGuid}/registrants`)}>
                        <VisibilityIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Edit'>
                      <IconButton size='small' onClick={() => navigate(`/events/${event.eventGuid}/edit`)}>
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Delete'>
                      <IconButton size='small' color='error' onClick={() => setDeleteTarget(event)}>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete event?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            &quot;{deleteTarget?.name}&quot; will be permanently deleted. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color='error' variant='contained' disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
